/**
 * Ant Design theme, built from the design tokens (src/theme/tokens.js).
 * Spec: docs/superpowers/specs/2026-10-20-frontend-ui-redesign-design.md
 *
 * antd derives hover, active, background and border shades from each seed
 * colour, so the status seeds are the mid-tone "dot" colours rather than the
 * dark text colours. Components are styled here, through tokens, and never
 * by overriding .ant-* class names.
 */
import { color, status, type, radius, shadow, focus, font, size } from "@/theme/tokens";

export const antdTheme = {
  token: {
    colorPrimary: color.accent,
    colorLink: color.accent,
    colorSuccess: status.success.dot,
    colorWarning: status.warning.dot,
    colorError: status.danger.dot,
    colorInfo: status.info.dot,

    colorText: color.ink,
    colorTextSecondary: color["ink-muted"],
    colorTextTertiary: color["ink-faint"],
    colorTextQuaternary: color["ink-faint"],

    colorBgLayout: color.canvas,
    colorBgContainer: color.surface,
    colorBgElevated: color.surface,
    colorBgSpotlight: color.ink,

    colorBorder: color["line-strong"],
    colorBorderSecondary: color.line,
    colorSplit: color.line,

    controlItemBgHover: color.hover,
    controlItemBgActive: color["accent-subtle"],
    controlItemBgActiveHover: color["accent-subtle"],
    controlOutline: focus.outline,

    fontFamily: font.sans,
    fontFamilyCode: font.mono,
    fontSize: type.body.size,
    lineHeight: type.body.lineHeight / type.body.size,

    controlHeight: size.control,
    controlHeightSM: size.controlSM,
    controlHeightLG: size.controlLG,

    borderRadius: radius.control,
    borderRadiusSM: 4,
    borderRadiusLG: radius.card,
    boxShadowSecondary: shadow.popover,
    wireframe: false,
  },
  components: {
    Button: {
      primaryShadow: "none",
      defaultShadow: "none",
      dangerShadow: "none",
      fontWeight: 500,
    },
    // The "clean ledger" table; DataTable.module.css adds the frame and the header type.
    Table: {
      headerBg: color.sunken,
      headerColor: color["ink-muted"],
      headerSplitColor: "transparent",
      borderColor: color.line,
      rowHoverBg: color.hover,
      // No fill on the sorted column: the arrow (colorPrimary) says which one is
      // sorted. Hovering a sortable header still shows the hover fill.
      headerSortActiveBg: color.sunken,
      bodySortBg: color.surface,
      headerSortHoverBg: color.hover,
      fixedHeaderSortActiveBg: color.hover,
      footerBg: color.sunken,
      // Inside DataTable's 1 px frame of radius-card, so the header and footer
      // corners follow its inner curve.
      headerBorderRadius: radius.card - 1,
      cellPaddingBlock: 8,
      cellPaddingInline: 12,
      cellPaddingBlockMD: 8,
      cellPaddingInlineMD: 12,
      cellPaddingBlockSM: 8,
      cellPaddingInlineSM: 12,
    },
    Tabs: {
      itemColor: color["ink-muted"],
      itemHoverColor: color.ink,
      itemSelectedColor: color.ink,
      itemActiveColor: color.ink,
      inkBarColor: color.ink,
      cardBg: color.sunken,
    },
    Segmented: {
      trackBg: color.hover,
      itemColor: color["ink-muted"],
      itemHoverColor: color.ink,
      itemSelectedBg: color.surface,
      itemSelectedColor: color.ink,
    },
    Tag: {
      defaultBg: color.hover,
      defaultColor: color["ink-strong"],
    },
    Modal: {
      titleFontSize: type["dialog-title"].size,
    },
    Layout: {
      bodyBg: color.canvas,
    },
  },
};
